import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Grid,
    Button,
    Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ClarityPopup from '../superAdminPages/registerUserlist/ClarityPopup';
import UtmDetails from '../common/UtmDetails';

const StyledPaper = styled(Paper)(() => ({
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    backgroundColor: '#ffffff',
}));

const StyledTableContainer = styled(TableContainer)({
    overflowX: 'auto',
});

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.01)' },
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04) !important',
        transition: 'background-color 0.2s ease',
    },
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
}));

const HeaderCell = styled(TableCell)({
    fontWeight: 700,
    color: '#444',
    backgroundColor: '#f9fafb',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    padding: '16px',
});

const PlanPurchasesPage = () => {
    const [planPurchases, setPlanPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [emailFilter, setEmailFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [openClarity, setOpenClarity] = useState(false);
    const [clarityEmail, setClarityEmail] = useState('');

    const fetchPlanPurchases = async (pageNum = 1) => {
        setLoading(true);
        try {
            const token =
                localStorage.getItem('authToken') ||
                localStorage.getItem('token') ||
                sessionStorage.getItem('authToken');

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const params = new URLSearchParams();
            if (emailFilter) params.append('email', emailFilter);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', pageNum);
            params.append('limit', 10);

            const res = await fetch(`/api/v1/lead-credits/admin/analytics?${params.toString()}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });
            const data = await res.json();

            if (data.success && data.data) {
                if (data.data.planPurchases) setPlanPurchases(data.data.planPurchases);
                if (data.data.totalPlanPages) setTotalPages(data.data.totalPlanPages);
            }
        } catch (err) {
            console.error('Error fetching plan purchases:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [emailFilter, startDate, endDate]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchPlanPurchases(page);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [emailFilter, startDate, endDate, page]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 48, height: 48 }}>
                        <ShoppingBagIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight="800" sx={{ color: '#1a202c', mb: 0.5 }}>
                            Plan Purchases
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            All credit plan purchases by registered users
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchPlanPurchases} color="primary" sx={{ border: '1px solid #e2e8f0' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Filters and Summary chip */}
            {!loading && (
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search by Email"
                                variant="outlined"
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Start Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="End Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<ClearIcon />}
                                fullWidth
                                onClick={() => {
                                    setEmailFilter('');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                                disabled={!emailFilter && !startDate && !endDate}
                                sx={{ height: '40px' }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                    <Chip
                        label={`${planPurchases.length} total purchase${planPurchases.length !== 1 ? 's' : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                </Box>
            )}

            <StyledPaper elevation={0}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography color="text.secondary" fontWeight="500">
                            Fetching latest records...
                        </Typography>
                    </Box>
                ) : (
                    <StyledTableContainer>
                        <Table stickyHeader size="medium">
                            <TableHead>
                                <TableRow>
                                    <HeaderCell>Date & Time</HeaderCell>
                                    <HeaderCell>Customer</HeaderCell>
                                    <HeaderCell align="right">Price</HeaderCell>
                                    <HeaderCell>Plan Name</HeaderCell>
                                    <HeaderCell align="right">Credits Added</HeaderCell>
                                    <HeaderCell>UTM</HeaderCell>
                                    <HeaderCell align="center">Clarity</HeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {planPurchases.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <ShoppingBagIcon sx={{ fontSize: 40, color: '#ccc' }} />
                                                <Typography color="text.secondary">No purchase history found</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    planPurchases.map((record, idx) => (
                                        <StyledTableRow key={idx}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">
                                                    {new Date(record.purchase_date).toLocaleDateString(undefined, {
                                                        month: 'short', day: 'numeric', year: 'numeric',
                                                    })}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(record.purchase_date).toLocaleTimeString([], {
                                                        hour: '2-digit', minute: '2-digit',
                                                    })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="600">
                                                    {record.user?.email || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {record.user?.username || ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight="600" color="text.primary">
                                                    {record.plan?.price != null
                                                        ? `₹${parseFloat(record.plan.price).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`
                                                        : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={record.plan?.name || 'Standard Plan'}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ fontWeight: 500, borderColor: 'primary.light', color: 'primary.dark' }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="700" color="success.main">
                                                    +{record.plan?.credits_granted || 0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <UtmDetails row={record} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View in Clarity">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setClarityEmail(record.user?.email || '');
                                                            setOpenClarity(true);
                                                        }}
                                                        sx={{
                                                            color: '#1976d2',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(25,118,210,0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <AnalyticsIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
                {!loading && totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                )}
            </StyledPaper>
            <ClarityPopup
                open={openClarity}
                onClose={() => setOpenClarity(false)}
                email={clarityEmail}
            />
        </Box>
    );
};

export default PlanPurchasesPage;
